import { isRouteErrorResponse, useRouteError } from 'react-router';

import { Button } from '@/components/ui/Button';
import { appConfig } from '@/config/app.config';
import { isApiError } from '@/services/http/api-error';
import { logger } from '@/utils/logger';

import styles from './RouteErrorPage.module.css';

function describe(error: unknown): { title: string; detail: string | null } {
  if (isRouteErrorResponse(error)) {
    return { title: `${error.status} ${error.statusText}`, detail: null };
  }
  if (isApiError(error)) {
    return {
      title: 'The server could not complete that request',
      detail: error.requestId ? `${error.message} (request ${error.requestId})` : error.message,
    };
  }
  if (error instanceof Error) {
    return { title: 'Something went wrong', detail: error.message };
  }
  return { title: 'Something went wrong', detail: null };
}

/**
 * The router's `errorElement`, which React Router uses as the app-wide error boundary —
 * so there is no separate ErrorBoundary component.
 */
export function RouteErrorPage() {
  const error = useRouteError();
  const { title, detail } = describe(error);

  logger.error('Unhandled route error', error);

  return (
    <div className={styles.page}>
      <h1>{title}</h1>
      <p className={styles.message}>
        Try again. If it keeps happening, send us what is written below.
      </p>
      {/* The raw message is useful locally and noise in production. */}
      {detail !== null && !appConfig.app.isProduction && (
        <pre className={styles.detail}>{detail}</pre>
      )}
      <Button
        onClick={() => {
          window.location.assign('/');
        }}
      >
        Back to home
      </Button>
    </div>
  );
}
